import { markDone, setOnboardingDemoEnabled } from './state'

const submitClickHandlers = new WeakMap()
const confirmClickHandlers = new WeakMap()
const modalConfirmSelector = '.modal.show .modal-footer .btn-success'

function getClickTarget(element) {
  if (!element) return null
  return element.querySelector('button, a') || element
}

function refreshDriver(context, delay = 0) {
  window.setTimeout(() => {
    if (context && context.driver && context.driver.refresh) {
      context.driver.refresh()
    }
  }, delay)
}

function getModalConfirmButton() {
  return document.querySelector(modalConfirmSelector)
}

function isElementVisible(element) {
  if (!element) return false

  const rect = element.getBoundingClientRect()
  const style = window.getComputedStyle(element)

  return rect.width > 0
    && rect.height > 0
    && style.display !== 'none'
    && style.visibility !== 'hidden'
}

function moveToModalConfirmStep(context) {
  const startedAt = Date.now()
  const timeout = 2500
  const interval = 50

  const tryMoveNext = () => {
    if (isElementVisible(getModalConfirmButton())) {
      window.setTimeout(() => {
        if (!isElementVisible(getModalConfirmButton())) return

        if (context && context.driver && context.driver.moveNext) {
          context.driver.moveNext()
        }

        refreshDriver(context, 100)
        refreshDriver(context, 300)
      }, 250)
      return
    }

    if (Date.now() - startedAt < timeout) {
      window.setTimeout(tryMoveNext, interval)
    }
  }

  tryMoveNext()
}

function getSynopsisLanguageTarget() {
  return document.querySelector('[id^="mc-synopsis-languages-"]')
}

function getSynopsisTitleTarget() {
  return document.querySelector('[id^="synopsis-title-"]')
}

function getSynopsisPressQuotesTarget() {
  return document.querySelector('[id^="synopsis-press-quotes-"]')
}

function getSynopsisShortTarget() {
  return document.querySelector('[id^="synopsis-short-"]')
}

function getSynopsisLongTarget() {
  return document.querySelector('[id^="synopsis-long-"]')
}

function getInternationalContactTarget() {
  return document.querySelector('[id^="internationalContactInput-"]')
}

function getProductTitleTarget() {
  return document.querySelector('[id^="productTitleInput-"]')
}

function goToTab(selector) {
  const tab = document.querySelector(selector)
  if (tab) tab.click()
}

function smoothScroll(context) {
  const activeElement = document.querySelector('.driver-active-element')

  if (activeElement && activeElement.scrollIntoView) {
    activeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    })

    setTimeout(() => {
      if (context && context.driver && context.driver.refresh) {
        context.driver.refresh()
      }
    }, 250)
  }
}

function goNext(context) {
  if (!context?.driver) return

  context.driver.moveNext()

  setTimeout(() => {
    smoothScroll(context)
  }, 150)
}

function goPrev(context) {
  if (!context?.driver) return

  context.driver.movePrevious()

  setTimeout(() => {
    smoothScroll(context)
  }, 150)
}

// eslint-disable-next-line import/prefer-default-export
export function getMovieFormSteps() {
  return [
    {
      element: '.movie-form-tabs .nav-item:nth-child(2) .nav-link',
      popover: {
        title: 'Movie Info Tab',
        description: 'This is the Movie Info tab. Click Next to continue.',
        showButtons: ['next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(2) .nav-link')
          goNext(context)
        },
      },
    },
    {
      element: '#mc-original-titleInput',
      popover: {
        title: 'Original Title Input',
        description: 'Enter the original title here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(2) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '.movie-form-tabs .nav-item:nth-child(3) .nav-link',
      popover: {
        title: 'Release Dates Tab',
        description: 'This is the Release Dates tab. Click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(2) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '#rightsSelect',
      popover: {
        title: 'Rights Input',
        description: 'Select rights here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '#territorySelect',
      popover: {
        title: 'Territory Input',
        description: 'Select territories here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '#languageSelect',
      popover: {
        title: 'Language Input',
        description: 'Select languages here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '#plannedDatePicker',
      popover: {
        title: 'Planned Date Input',
        description: 'Select planned date here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '#actualDatePicker',
      popover: {
        title: 'Actual Date Input',
        description: 'Select actual date here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(4) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '.movie-form-tabs .nav-item:nth-child(4) .nav-link',
      popover: {
        title: 'Stills Tab',
        description: 'This is the Stills tab. Click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(4) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(3) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '#stillsCard',
      popover: {
        title: 'Stills Input',
        description: 'Manage stills here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(4) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '.movie-form-tabs .nav-item:nth-child(5) .nav-link',
      popover: {
        title: 'Synopsis Tab',
        description: 'This is the Synopsis tab. Click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(4) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: () => getSynopsisLanguageTarget(),
      popover: {
        title: 'Synopsis Language Input',
        description: 'Select synopsis language here, then click Next.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: () => getSynopsisTitleTarget(),
      popover: {
        title: 'Synopsis Title Input',
        description: 'Enter synopsis title here, then click Next.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: () => getSynopsisPressQuotesTarget(),
      popover: {
        title: 'Press Quotes Input',
        description: 'Fill press quotes here, then click Next.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: () => getSynopsisShortTarget(),
      popover: {
        title: 'Short Synopsis Input',
        description: 'Fill short synopsis here, then click Next.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: () => getSynopsisLongTarget(),
      popover: {
        title: 'Long Synopsis Input',
        description: 'Fill long synopsis here, then click Next.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(6) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '.movie-form-tabs .nav-item:nth-child(6) .nav-link',
      popover: {
        title: 'Materials Tab',
        description: 'This is the Materials tab. Click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(6) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(5) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '#materialsInput',
      popover: {
        title: 'Material Input',
        description: 'Select the material here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(7) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(6) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '.movie-form-tabs .nav-item:nth-child(7) .nav-link',
      popover: {
        title: 'International Contacts Tab',
        description: 'This is the International Contacts tab. Click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(7) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(6) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: () => getInternationalContactTarget(),
      popover: {
        title: 'International Contact Input',
        description: 'Select the international contact here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(8) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(7) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '.movie-form-tabs .nav-item:nth-child(8) .nav-link',
      popover: {
        title: 'Trailer Tab',
        description: 'This is the Trailer tab. Click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(8) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(7) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '#trailerLanguageInput',
      popover: {
        title: 'Trailer Language Input',
        description: 'Select trailer language here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(9) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(8) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '.movie-form-tabs .nav-item:nth-child(9) .nav-link',
      popover: {
        title: 'Products Tab',
        description: 'This is the Products tab. Click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(9) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(8) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: () => getProductTitleTarget(),
      popover: {
        title: 'Product Title Input',
        description: 'Enter the product title here, then click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(10) .nav-link')
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(9) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '.movie-form-tabs .nav-item:nth-child(10) .nav-link',
      popover: {
        title: 'Website Preview Tab',
        description: 'This is the Website Preview tab. Click Next to continue.',
        showButtons: ['previous', 'next'],
        onNextClick: (el, step, context) => {
          goNext(context)
        },
        onPrevClick: (el, step, context) => {
          goToTab('.movie-form-tabs .nav-item:nth-child(9) .nav-link')
          goPrev(context)
        },
      },
    },
    {
      element: '[data-tour="movie-form-submit-btn"]',
      disableActiveInteraction: false,
      popover: {
        title: 'Submit Movie',
        description: 'Click submit to open the confirmation modal.',
        showButtons: ['previous'],
      },
      onHighlighted: (element, step, context) => {
        const clickTarget = getClickTarget(element)
        if (!clickTarget) return

        const handler = () => {
          moveToModalConfirmStep(context)
        }

        clickTarget.addEventListener('click', handler)
        submitClickHandlers.set(clickTarget, handler)
      },
      onDeselected: element => {
        const clickTarget = getClickTarget(element)
        if (clickTarget && submitClickHandlers.has(clickTarget)) {
          clickTarget.removeEventListener('click', submitClickHandlers.get(clickTarget))
          submitClickHandlers.delete(clickTarget)
        }
      },
    },
    {
      element: () => getModalConfirmButton(),
      disableActiveInteraction: false,
      popover: {
        title: 'Confirm Submit',
        description: 'Click YES in this confirmation modal to finish onboarding.',
        side: 'top',
        align: 'center',
        showButtons: ['previous'],
      },
      onHighlightStarted: (element, step, context) => {
        const clickTarget = getClickTarget(element)
        if (!clickTarget) return

        const handler = () => {
          markDone('movie-form')
          localStorage.removeItem('onboarding_next')
          sessionStorage.removeItem('onboarding_next')
          setOnboardingDemoEnabled(false)

          if (context && context.driver && context.driver.destroy) {
            context.driver.destroy()
          }
        }

        clickTarget.addEventListener('click', handler)
        confirmClickHandlers.set(clickTarget, handler)
      },
      onHighlighted: (element, step, context) => {
        const clickTarget = getClickTarget(element)
        if (!clickTarget) return

        refreshDriver(context, 0)
        refreshDriver(context, 150)
        refreshDriver(context, 350)
        clickTarget.focus()
      },
      onDeselected: element => {
        const clickTarget = getClickTarget(element)
        if (clickTarget && confirmClickHandlers.has(clickTarget)) {
          clickTarget.removeEventListener('click', confirmClickHandlers.get(clickTarget))
          confirmClickHandlers.delete(clickTarget)
        }
      },
    },
  ]
}
